import React, { useEffect } from 'react';
import { useForm, FormProvider, Controller, useFieldArray } from 'react-hook-form';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    ButtonAddRounded,
    ButtonEdit,
    ButtonDelete,
    ButtonClose
} from '../../../components/Buttons';
import { ModalDialog } from '../../../components/ModalDialog';
import { Checkbox } from '../../../../components/PrimereactComponents';
import { RHFInputText, RHFCalendar, RHFDropdown } from '../../../components/rhf';
import { FormInput, Label } from '../../../components/form';
import { ZASTUPITELSTVO } from '../../../constants/field_lengths';
import { maxLengthMessage } from '../../../utils/errors';
import { InputError } from '../../../components/InputError';

export const LeadershipFormDialog = ({
    members,
    leadership,
    editMember,
    isVisible,
    onHide,
    onSubmit
}) => {
    const edit = !!editMember;

    const methods = useForm({
        defaultValues: editMember || { leadership_roles: [] }
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = methods;

    const {
        fields,
        append,
        remove
    } = useFieldArray({
        control,
        name: 'leadership_roles'
    })

    const leadershipRoles = fields;
    const selectedMemberId = methods.watch('member');

    useEffect(() => {
        if (isVisible) {
            if (edit) {
                reset({
                    member: editMember.id,
                    leadership_roles: editMember.leadership_roles || []
                });
            } else {
                reset({ member: null, leadership_roles: [] });
            }
        }
    }, [isVisible, edit, editMember, reset]);

    useEffect(() => {
        if (isVisible && !edit && leadershipRoles.length === 0) {
            append({
                role: '',
                leader: false,
                date_from: null,
                date_to: null
            });
        }
    }, [isVisible, edit, leadershipRoles, append]);

    useEffect(() => {
        if (!selectedMemberId) return;

        const leadershipData = leadership.find((data) => data.id === selectedMemberId);

        if (leadershipData && leadershipData?.leadership_roles.length > 0) {
            const formattedRoles = leadershipData.leadership_roles.map(role => ({
                ...role,
                date_from: role.date_from ? new Date(role.date_from) : null,
                date_to: role.date_to ? new Date(role.date_to) : null,
            }));

            reset({
                member: selectedMemberId,
                leadership_roles: formattedRoles
            });

        } else {
            reset({
                member: selectedMemberId,
                leadership_roles: []
            })
        }
    }, [selectedMemberId, leadership, reset]);

    const onClickAddRole = () => {
        append({
            role: '',
            leader: false,
            date_from: null,
            date_to: null
        });
    };

    const leaderTemplate = (rowData, options) => {
        const idx = options.rowIndex;
        return (
            <Controller
                control={control}
                name={`leadership_roles.${idx}.leader`}
                render={({ field }) => (
                    <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.checked)}
                    />
                )}
            />
        );
    };

    const roleTemplate = (rowData, options) => {
        const idx = options.rowIndex;
        return (
            <FormInput>
                <RHFInputText
                    control={control}
                    name={`leadership_roles.${idx}.role`}
                    placeholder='Název pozice'
                    rules={{
                        required: 'Vyplňte název pozice',
                        maxLength: {
                            value: ZASTUPITELSTVO.VEDENI_FUNKCE,
                            message: maxLengthMessage(ZASTUPITELSTVO.VEDENI_FUNKCE)
                        }
                    }}
                />
            </FormInput>
        );
    };

    const dateFromTemplate = (rowData, options) => {
        const idx = options.rowIndex;
        return (
            <FormInput>
                <RHFCalendar
                    control={control}
                    name={`leadership_roles.${idx}.date_from`}
                    placeholder='Datum od'
                    inputProps={{
                        dateFormat: 'd.m.yy'
                    }}
                />
            </FormInput>
        );
    };

    const dateToTemplate = (rowData, options) => {
        const idx = options.rowIndex;
        return (
            <FormInput>
                <RHFCalendar
                    control={control}
                    name={`leadership_roles.${idx}.date_to`}
                    placeholder='Datum do'
                    rules={{
                        validate: {
                            afterStart: (value) => {
                                const start = methods.getValues(`leadership_roles.${idx}.date_from`);
                                if (start && value && start > value) {
                                    return 'Datum do nesmí začínat před datem od';
                                }
                                return true;
                            }
                        }
                    }}
                    inputProps={{
                        dateFormat: 'd.m.yy'
                    }}
                />
                {errors?.leadership_roles?.[idx]?.date_to?.message && (
                    <InputError message={errors.leadership_roles[idx].date_to.message} />
                )}
            </FormInput>
        );
    };

    const removeRoleTemplate = (rowData, options) => {
        return (
            <ButtonDelete
                rounded
                title="Odstranit pozici"
                onClick={() => remove(options.rowIndex)}
            />
        );
    };

    return (
        <FormProvider {...methods}>
            <ModalDialog
                visible={isVisible}
                header={
                    edit
                        ? 'Upravit pozice člena ve vedení'
                        : 'Přidat pozice člena ve vedení'
                }
                style={{ width: 800 }}
                footer={
                    <>
                        <ButtonClose label='Zavřít' onClick={onHide} />
                        <ButtonEdit
                            label='Vložit do tabulky'
                            type='button'
                            onClick={handleSubmit(onSubmit)}
                        />
                    </>
                }
                onHide={onHide}
            >
                <Label text='Člen' required/>
                <RHFDropdown
                    control={control}
                    name='member'
                    placeholder='Zvolte člena'
                    inputProps={{
                        options: (
                            edit
                            ? members
                            : members.filter(
                                (member) => !leadership.some((l) => l.id === member.id)
                            )
                        ).map(member => ({
                          ...member,
                          label: `${member.label} (ID: ${member.id})`
                        })),
                        optionValue: 'id',
                        emptyMessage: 'Nenalezeni žádní členové pro funkční období',
                        filter: true,
                        disabled: edit
                    }}
                />

                <div style={{ marginTop: '0.5rem' }}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <DataTable
                            value={leadershipRoles}
                            stripedRows
                            size='small'
                            emptyMessage='Nejsou zapsány žádné pozice'
                            tableStyle={{ minWidth: '100%' }}
                        >
                            <Column header='Pozice' body={roleTemplate} />
                            <Column header='Lídr' body={leaderTemplate} style={{ textAlign: 'center' }} />
                            <Column header='Datum od' body={dateFromTemplate} />
                            <Column header='Datum do' body={dateToTemplate} />
                            <Column body={removeRoleTemplate} />
                        </DataTable>
                    </form>
                </div>

                <div style={{ marginTop: '0.5rem' }}>
                    <ButtonAddRounded
                        rounded
                        title='Přidat pozici'
                        onClick={() => onClickAddRole()}
                    />
                </div>
            </ModalDialog>
        </FormProvider>
    );
};
